import { ref, onMounted } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { ColDef } from 'ag-grid-community'

export interface AutocompleteItem {
  value: any
  label: string
  group?: string
  [key: string]: any
}

export interface AutocompleteFetchParams {
  cellEditor: any
  text: string
  update: (items: AutocompleteItem[]) => void
}

export interface AutocompleteSelectParams {
  cellEditor: any
  selectedItem: AutocompleteItem
}

export interface AutocompleteConfig {
  debounceWaitMs?: number
  fetch: (cellEditor: any, text: any, update: (items: AutocompleteItem[]) => void) => Promise<void> | void
  onSelect?: (cellEditor: any, selectedItem: AutocompleteItem) => Promise<void> | void
  emptyMsg?: string
  showOnFocus?: boolean
  strict?: boolean
  onFreeTextSelect?: (cellEditor: any, userInput: string) => any
  minLength?: number
  className?: string
  render?: (cellEditor: any, item: AutocompleteItem, currentValue: string) => HTMLElement
  renderGroup?: (cellEditor: any, name: string, currentValue: string) => HTMLElement
}

export interface AutocompleteColumnConfig {
  field: string
  autocomplete: AutocompleteConfig
  placeholder?: string
  valueParser?: (params: any) => any
  valueFormatter?: (params: any) => string
}

export interface SimpleAutocompleteConfig {
  field: string
  placeholder?: string
  debounceMs?: number
  minLength?: number
  emptyMsg?: string
  showOnFocus?: boolean
  strict?: boolean
  fetchData: (searchText: string) => Promise<AutocompleteItem[]> | AutocompleteItem[]
  onSelect?: (selectedItem: AutocompleteItem) => Promise<void> | void
  valueParser?: (params: any) => any
  valueFormatter?: (params: any) => string
}

export const useAgGridAutocomplete = () => {
  // Client-side only imports
  let AutocompleteSelectCellEditor: any = null
  let CustomEscapeAwareEditor: any = null
  const isLoaded = ref(false)

  onMounted(async () => {
    if (import.meta.client) {
      try {
        const { AutocompleteSelectCellEditor: ImportedEditor } = await import('ag-grid-autocomplete-editor')
        AutocompleteSelectCellEditor = ImportedEditor
        
        // Create custom editor that handles Escape key properly
        CustomEscapeAwareEditor = class extends ImportedEditor {
          private initialValue: any
          
          constructor() {
            super()
          }
          
          override init(params: any) {
            super.init(params)
            this.initialValue = params.value
            
            // Access eInput through (this as any) to bypass private access
            const input = (this as any).eInput
            if (input) {
              input.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                  event.stopPropagation()
                  event.preventDefault()
                  params.api.stopEditing()
                  params.api.setFocusedCell(params.rowIndex, params.column)
                }
              })
            }
          }
          
          override getValue() {
            if ((this as any).currentItem) {
              return (this as any).currentItem
            }
            const input = (this as any).eInput
            return (input?.value) || this.initialValue
          }
        }
        
        isLoaded.value = true
      } catch (error) {
        console.error('Failed to load ag-grid-autocomplete-editor:', error)
      }
    }
  })

  // Transform autocomplete config to AG Grid cellEditorParams
  const createCellEditorParams = (config: AutocompleteConfig) => ({
    autocomplete: {
      debounceWaitMs: config.debounceWaitMs ?? 100,
      fetch: config.fetch,
      onSelect: config.onSelect,
      emptyMsg: config.emptyMsg ?? 'No results found',
      showOnFocus: config.showOnFocus ?? false,
      strict: config.strict ?? true,
      onFreeTextSelect: config.onFreeTextSelect,
      minLength: config.minLength ?? 1,
      className: config.className,
      render: config.render,
      renderGroup: config.renderGroup
    },
    placeholder: 'Search...'
  })

  // Create AG Grid column definitions with autocomplete
  const createAutocompleteColumns = (baseColumns: ColDef[], autocompleteConfigs: AutocompleteColumnConfig[]) => {
    return baseColumns.map(col => {
      const autocompleteConfig = autocompleteConfigs.find(config => config.field === col.field)
      
      if (autocompleteConfig) {
        return {
          ...col,
          cellEditor: CustomEscapeAwareEditor || AutocompleteSelectCellEditor,
          cellEditorParams: {
            ...createCellEditorParams(autocompleteConfig.autocomplete),
            placeholder: autocompleteConfig.placeholder
          },
          valueParser: autocompleteConfig.valueParser || ((params: any) => {
            if (typeof params.newValue === 'object') {
              return params.newValue?.label || params.newValue?.value || ''
            }
            return params.newValue || ''
          }),
          valueFormatter: autocompleteConfig.valueFormatter || ((params: any) => {
            if (typeof params.value === 'object') {
              return params.value?.label || params.value?.value || ''
            }
            return params.value || ''
          })
        }
      }
      
      return col
    })
  }

  // Simplified API for common use cases
  const createSimpleAutocomplete = (config: SimpleAutocompleteConfig): AutocompleteColumnConfig => {
    return {
      field: config.field,
      placeholder: config.placeholder,
      valueParser: config.valueParser,
      valueFormatter: config.valueFormatter,
      autocomplete: {
        debounceWaitMs: config.debounceMs ?? 100,
        minLength: config.minLength ?? 1,
        emptyMsg: config.emptyMsg ?? 'No results found',
        showOnFocus: config.showOnFocus ?? false,
        strict: config.strict ?? true,
        fetch: async (cellEditor: any, text: any, update: any) => {
          try {
            if (!text || text.trim() === '') {
              update([])
              return
            }
            
            const results = await config.fetchData(text.trim())
            update(Array.isArray(results) ? results : [])
          } catch (error) {
            console.error(`Error fetching autocomplete data for ${config.field}:`, error)
            update([])
          }
        },
        onSelect: config.onSelect ? async (cellEditor: any, selectedItem: any) => {
          await config.onSelect!(selectedItem)
        } : undefined
      }
    }
  }

  // Helper for static data autocomplete
  const createStaticAutocomplete = (
    field: string, 
    data: AutocompleteItem[], 
    options: Partial<SimpleAutocompleteConfig> = {}
  ): AutocompleteColumnConfig => {
    return createSimpleAutocomplete({
      field,
      ...options,
      fetchData: (searchText: string) => {
        const lowerSearch = searchText.toLowerCase()
        return data.filter(item => 
          item.label.toLowerCase().includes(lowerSearch) ||
          item.value.toString().toLowerCase().includes(lowerSearch)
        )
      }
    })
  }

  // Helper for API-based autocomplete
  const createApiAutocomplete = (
    field: string,
    apiUrl: string | ((searchText: string) => string),
    options: Partial<SimpleAutocompleteConfig> & {
      transform?: (response: any) => AutocompleteItem[]
    } = {}
  ): AutocompleteColumnConfig => {
    return {
      field,
      placeholder: options.placeholder || `Search ${field}...`,
      autocomplete: {
        debounceWaitMs: options.debounceMs ?? 300,
        minLength: options.minLength ?? 1,
        emptyMsg: options.emptyMsg || 'No results found',
        showOnFocus: options.showOnFocus ?? false,
        strict: options.strict ?? true,
        fetch: async (cellEditor: any, text: any, update: any) => {
          try {
            const searchText = text?.trim() || '';
            
            // Skip API calls if searchText is empty or just whitespace
            if (!searchText || searchText.length < (options.minLength ?? 1)) {
              update([]);
              return;
            }
            
            // Make API call
            const url = typeof apiUrl === 'function' ? apiUrl(searchText) : `${apiUrl}?q=${encodeURIComponent(searchText)}`
            const response = await $fetch(url)
            
            let results: AutocompleteItem[] = []
            
            if (options.transform) {
              results = options.transform(response)
            } else {
              // Default transformation - assumes response is array of objects with label/value
              if (Array.isArray(response)) {
                results = response.map(item => ({
                  label: item.label || item.name || item.title || String(item),
                  value: item.value || item.id || item,
                  ...item
                }))
              }
            }
            
            update(results);
            
          } catch (error) {
            console.error(`Error fetching from API for ${field}:`, error)
            update([]);
          }
        },
        onSelect: options.onSelect ? async (cellEditor: any, selectedItem: any) => {
          return options.onSelect!(selectedItem);
        } : undefined
      },
      valueParser: options.valueParser,
      valueFormatter: options.valueFormatter
    }
  }

  // Specialized botanical name autocomplete that combines GBIF and AI spell checking

  const createBotanicalNameAutocomplete = (
    options: {
      existingNames?: string[]
      addressComponents?: Record<string, any>
      debounceMs?: number
      minLength?: number
    } = {}
  ): AutocompleteColumnConfig => {
    return {
      field: 'botanicalname',
      placeholder: 'Search botanical names...',
      autocomplete: {
        debounceWaitMs: options.debounceMs ?? 30, // OPTIMIZATION: Reduced from 50ms to 30ms for even faster GBIF response
        minLength: options.minLength ?? 1,
        emptyMsg: 'No botanical names found',
        showOnFocus: false,
        strict: false, // Allow free text entry
        // Custom render function to add AI badge for AI suggestions
        render: (cellEditor: any, item: AutocompleteItem, currentValue: string) => {
          console.log('[Autocomplete Render] Item:', item, 'Group:', item.group, 'Source:', item.source)
          
          // Check if this is an AI suggestion
          const isAISuggestion = item.group?.toLowerCase().includes('ai') || 
                                 item.source?.includes('gemini') || 
                                 item.source?.includes('ai')
          
          console.log('[Autocomplete Render] Is AI suggestion:', isAISuggestion)
          
          // Create simple item with AI badge if needed
          const div = document.createElement('div')
          div.style.display = 'flex'
          div.style.alignItems = 'center'
          div.style.justifyContent = 'space-between'
          div.style.padding = '4px 8px'
          
          // Species name
          const nameSpan = document.createElement('span')
          nameSpan.textContent = item.label || item.value
          div.appendChild(nameSpan)
          
          if (isAISuggestion) {
            console.log('[Autocomplete Render] Adding AI badge for:', item.label || item.value)
            // Simple AI badge
            const badge = document.createElement('span')
            badge.innerHTML =`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 48 48" style="margin-right: 3px;"><!-- Icon from Health Icons by Resolve to Save Lives - https://github.com/resolvetosavelives/healthicons/blob/main/LICENSE --><path fill="white" d="M34 6c-1.368 4.944-3.13 6.633-8 8c4.87 1.367 6.632 3.056 8 8c1.368-4.944 3.13-6.633 8-8c-4.87-1.367-6.632-3.056-8-8m-14 8c-2.395 8.651-5.476 11.608-14 14c8.524 2.392 11.605 5.349 14 14c2.395-8.651 5.476-11.608 14-14c-8.524-2.392-11.605-5.349-14-14"/></svg><span style="font-weight: 500; font-size: 11px; color:white;">AI</span>`
            badge.style.background = 'linear-gradient(45deg, #8b5cf6, #ec4899)'
            badge.style.color = 'white'
            badge.style.padding = '3px 8px'
            badge.style.borderRadius = '12px'
            badge.style.marginLeft = '8px'
            badge.style.display = 'flex'
            badge.style.alignItems = 'center'
            badge.style.justifyContent = 'center'
            badge.style.height = '20px'
            badge.style.maxWidth = '42px'

            div.appendChild(badge)
          }
          
          return div
        },
        fetch: async (cellEditor: any, text: any, update: any) => {
          try {
            let match = text?.toLowerCase() || '';
            
            console.log('[GBIF Autocomplete] PRIORITY: Starting GBIF search for:', match);
            
            // Skip API calls if match is empty or just whitespace
            if (!match || match.trim() === '') {
              update([]);
              return;
            }
            
            // OPTIMIZATION: Use cached server-side GBIF API endpoints
            // Track all results
            let allItems: AutocompleteItem[] = [];

            // PRIORITY 1: GBIF suggest API via cached server endpoint
            const gbifPromises = [];
            
            // Use our cached server-side GBIF suggest endpoint
            gbifPromises.push(
              $fetch<{results: AutocompleteItem[]}>('/api/gbif/autocomplete', {
                query: {
                  q: match,
                  type: 'suggest',
                  limit: 20
                }
              })
                .then((response) => {
                  console.log('[GBIF Autocomplete] CACHED suggest results received:', response.results?.length || 0, 'items');
                  return response.results || [];
                })
                .catch((suggestError) => {
                  console.error('[GBIF Autocomplete] Error with cached GBIF suggest API:', suggestError);
                  return [];
                })
            );

            // PRIORITY 2: GBIF search API via cached server endpoint (for longer queries)
            if (match.length > 2) {
              gbifPromises.push(
                $fetch<{results: AutocompleteItem[]}>('/api/gbif/autocomplete', {
                  query: {
                    q: match,
                    type: 'search',
                    limit: 10
                  }
                })
                .then((response) => {
                  console.log('[GBIF Autocomplete] CACHED search results received:', response.results?.length || 0, 'items');
                  return response.results || [];
                })
                .catch((searchError) => {
                  console.error('[GBIF Autocomplete] Error with cached GBIF search API:', searchError);
                  return [];
                })
              );
            }

            // OPTIMIZATION: Process GBIF results immediately and show partial results
            Promise.allSettled(gbifPromises).then((gbifResults) => {
              const immediateItems: AutocompleteItem[] = [];
              
              gbifResults.forEach((result) => {
                if (result.status === 'fulfilled' && result.value) {
                  result.value.forEach((item: AutocompleteItem) => {
                    if (!immediateItems.some(existing => existing.value === item.value)) {
                      immediateItems.push(item);
                    }
                  });
                }
              });
              
              if (immediateItems.length > 0) {
                console.log('[GBIF Autocomplete] Showing immediate GBIF results:', immediateItems.length, 'items');
                // Sort by rank priority for immediate display
                immediateItems.sort((a, b) => getRankPriority(a.group || '') - getRankPriority(b.group || ''));
                // Show immediate results without waiting for AI suggestions
                update(immediateItems);
              }
            });

            // SECONDARY: Start AI spell check in background (lower priority)
            const spellCheckPromise = spellCheckBotanicalName(match, options.existingNames, options.addressComponents);

            // Process all GBIF results first
            const gbifResults = await Promise.allSettled(gbifPromises);
            gbifResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                result.value.forEach((item: AutocompleteItem) => {
                  if (!allItems.some(existing => existing.value === item.value)) {
                    allItems.push(item);
                  }
                });
              }
            });
            
            // TERTIARY: Add AI suggestions (background processing)
            try {
              const spellCheckResults = await spellCheckPromise;
              
              if (spellCheckResults && spellCheckResults.length > 0) {
                console.log('[GBIF Autocomplete] Raw AI results:', spellCheckResults);
                
                const normalizedAIResults = spellCheckResults
                  .filter(item => {
                    // Filter out null, empty, or "nothing found" results
                    const value = item.value || '';
                    return value && 
                           value !== 'null' && 
                           value.toLowerCase() !== 'nothing found' &&
                           value.trim() !== '';
                  })
                  .map(item => ({
                    ...item,
                    group: normalizeRank(item.group || 'AI Suggestion'),
                    source: 'gemini-ai' // Add explicit source for easier detection
                  }));
                
                console.log('[GBIF Autocomplete] Normalized AI results:', normalizedAIResults);
                
                normalizedAIResults.forEach(item => {
                  if (!allItems.some(existing => existing.value === item.value)) {
                    console.log('[GBIF Autocomplete] Adding AI suggestion to results:', item);
                    allItems.push(item);
                  }
                });
              }
            } catch (aiError) {
              console.error('[GBIF Autocomplete] Error with AI spell check:', aiError);
            }
            
            // Final update with all results (GBIF + AI suggestions)
            console.log('[GBIF Autocomplete] Final update with all results:', allItems.length, 'total items');
            
            // Sort all items by rank priority
            allItems.sort((a, b) => getRankPriority(a.group || '') - getRankPriority(b.group || ''));
            
            // Return combined results or show empty if no results
            update(allItems.length > 0 ? allItems : []);
          } catch (error) {
            console.error('Error fetching botanical name data:', error);
            update([]);
          }
        },
        onSelect: async (cellEditor: any, selectedItem: any) => {
          console.log('[DEBUG] botanicalName onSelect called with:', selectedItem);
          
          if (!selectedItem) {
            console.warn('[DEBUG] No selectedItem in onSelect, skipping processing');
            return;
          }
          
          // Log genus and family information
          if (selectedItem.genus) {
            console.log(`[BotanicalName.onSelect] Found genus '${selectedItem.genus}' in response`);
          }
          
          if (selectedItem.family) {
            console.log(`[BotanicalName.onSelect] Found family '${selectedItem.family}' in response`);
          }
          
          // Set the cell value immediately with autocomplete source marking
          const valueToSet = {
            label: selectedItem.value || selectedItem.label || '',
            value: selectedItem.value || selectedItem.label || '',
            source: 'autocomplete-selection',
            genus: selectedItem.genus || '',
            family: selectedItem.family || '',
            updatedAt: new Date().toISOString()
          };
          (cellEditor as any).currentItem = valueToSet;
          
          // Let the cell response system handle all external API calls automatically
          // The botanical cell response system will detect the change and trigger the appropriate transformations
          const botanicalName = selectedItem.value || selectedItem.label || ''
          console.log('[GBIF Autocomplete] Botanical name selected. Cell response system will handle external API calls for:', botanicalName)
        },
        onFreeTextSelect: (cellEditor: any, userInput: string) => {
          console.log('[DEBUG] botanicalName onFreeTextSelect called with:', userInput);
          
          // Mark free text entries with special source
          return {
            label: userInput,
            value: userInput,
            source: 'free-text-entry',
            genus: '',
            family: '',
            updatedAt: new Date().toISOString()
          };
        }
      }
    }
  }

  // Specialized common name autocomplete using GBIF API
  const createCommonNameAutocomplete = (
    options: {
      debounceMs?: number
      minLength?: number
    } = {}
  ): AutocompleteColumnConfig => {
    return {
      field: 'commonname',
      placeholder: 'Search common names...',
      autocomplete: {
        debounceWaitMs: options.debounceMs ?? 100,
        minLength: options.minLength ?? 1,
        emptyMsg: 'No common names found',
        showOnFocus: false,
        strict: false, // Allow free text entry
        fetch: async (cellEditor: any, text: any, update: any) => {
          try {
            const searchText = text?.trim() || '';
            
            console.log('[CommonName Autocomplete] Starting GBIF search for:', searchText);
            
            // Skip API calls if searchText is empty or just whitespace
            if (!searchText || searchText.length < (options.minLength ?? 1)) {
              update([]);
              return;
            }
            
            // Build GBIF API URL with the specified parameters
            const params = new URLSearchParams({
              q: searchText,
              qField: 'VERNACULAR',
              rank: 'species',
              status: 'ACCEPTED',
              datasetKey: 'd7dddbf4-2cf0-4f39-9b2a-bb099caae36c',
              higherTaxonKey: '6', // Plantae kingdom
              limit: '10' // Increased from 1 to get more results
            });
            
            const gbifUrl = `https://api.gbif.org/v1/species/search?${params.toString()}`;
            
            console.log('[CommonName Autocomplete] Fetching from GBIF:', gbifUrl);
            
            const response = await $fetch<{
              results: Array<{
                key: number;
                scientificName: string;
                canonicalName: string;
                vernacularNames?: Array<{
                  vernacularName: string;
                  language?: string;
                  source?: string;
                }>;
                genus?: string;
                family?: string;
                kingdom?: string;
                phylum?: string;
              }>
            }>(gbifUrl, {
              timeout: 8000
            });
            
            console.log('[CommonName Autocomplete] GBIF response received:', response.results?.length || 0, 'species');
            
            if (response?.results?.length > 0) {
              const commonNameItems: AutocompleteItem[] = [];
              
              response.results.forEach(species => {
                if (species.vernacularNames && species.vernacularNames.length > 0) {
                  // Filter vernacular names that match the search text
                  const matchingVernaculars = species.vernacularNames.filter(vn => 
                    vn.vernacularName?.toLowerCase().includes(searchText.toLowerCase())
                  );
                  
                  matchingVernaculars.forEach(vernacular => {
                    // Avoid duplicates
                    if (!commonNameItems.some(item => item.value === vernacular.vernacularName)) {
                      commonNameItems.push({
                        value: vernacular.vernacularName,
                        label: vernacular.vernacularName,
                        group: 'Common Names',
                        scientificName: species.canonicalName || species.scientificName,
                        genus: species.genus,
                        family: species.family,
                        language: vernacular.language,
                        source: 'gbif-vernacular',
                        key: `${species.key}-${vernacular.vernacularName}`
                      });
                    }
                  });
                }
              });
              
              console.log('[CommonName Autocomplete] Processed', commonNameItems.length, 'common name items');
              
              // Sort by relevance (exact matches first, then partial matches)
              commonNameItems.sort((a, b) => {
                const aExact = a.label.toLowerCase() === searchText.toLowerCase() ? 0 : 1;
                const bExact = b.label.toLowerCase() === searchText.toLowerCase() ? 0 : 1;
                if (aExact !== bExact) return aExact - bExact;
                
                const aStarts = a.label.toLowerCase().startsWith(searchText.toLowerCase()) ? 0 : 1;
                const bStarts = b.label.toLowerCase().startsWith(searchText.toLowerCase()) ? 0 : 1;
                return aStarts - bStarts;
              });
              
              update(commonNameItems);
            } else {
              console.log('[CommonName Autocomplete] No vernacular names found in GBIF response');
              update([]);
            }
          } catch (error) {
            console.error('[CommonName Autocomplete] Error fetching common names from GBIF:', error);
            update([]);
          }
        },
        onSelect: async (cellEditor: any, selectedItem: any) => {
          console.log('[CommonName Autocomplete] onSelect called with:', selectedItem);
          
          if (!selectedItem) {
            console.warn('[CommonName Autocomplete] No selectedItem in onSelect');
            return;
          }
          
          // Set the cell value with metadata from GBIF
          const valueToSet = {
            label: selectedItem.value || selectedItem.label || '',
            value: selectedItem.value || selectedItem.label || '',
            source: 'autocomplete-selection',
            scientificName: selectedItem.scientificName || '',
            genus: selectedItem.genus || '',
            family: selectedItem.family || '',
            language: selectedItem.language || '',
            updatedAt: new Date().toISOString()
          };
          (cellEditor as any).currentItem = valueToSet;
          
          console.log('[CommonName Autocomplete] Common name selected:', selectedItem.value);
        },
        onFreeTextSelect: (cellEditor: any, userInput: string) => {
          console.log('[CommonName Autocomplete] onFreeTextSelect called with:', userInput);
          
          // Mark free text entries with special source
          return {
            label: userInput,
            value: userInput,
            source: 'free-text-entry',
            scientificName: '',
            genus: '',
            family: '',
            language: '',
            updatedAt: new Date().toISOString()
          };
        }
      }
    }
  }

  // Helper function to normalize rank names to prevent duplicate sections
  const normalizeRank = (rank: string = ''): string => {
    return rank.charAt(0).toUpperCase() + rank.slice(1).toLowerCase();
  };

  // Helper function to sort by taxonomic rank priority
  const getRankPriority = (rank: string = ''): number => {
    const priority: {[key: string]: number} = {
      'ai suggestion': 0,
      'species': 1,
      'variety': 2,
      'subspecies': 3,
      'genus': 4,
      'family': 5
    };
    const lowerRank = rank.toLowerCase();
    return priority[lowerRank] || 999;
  };

  // Internal spell check function (non-debounced)
  const _spellCheckBotanicalNameInternal = async (
    match: string,
    existingNames: string[] = [],
    addressComponents?: Record<string, any>
  ): Promise<AutocompleteItem[]> => {
    try {
      console.log('[spellCheck] Starting spell check for:', match);
      
      if (!match || match.trim() === '' || match.length < 3) {
        return [];
      }

      // Try up to 3 times with exponential backoff
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const spellCheckResponse = await $fetch<{botanicalname: string, rank?: string, text?: string}>('/api/ai/gmni', {
            method: 'POST',
            body: {
              prompts: [
                {
                  hidden: true,
                  prompt: 'spellcheckPlantNamePrompt'
                },
                {
                  hidden: false,
                  prompt: match
                },
                // Extract plant names from existing rows
                ...(existingNames.length ? [{
                  hidden: true,
                  prompt: 'spellcheckPlantNamePrompt2',
                  arg: existingNames
                }] : []),
                // Only include location prompt if we have valid address components
                ...(addressComponents && typeof addressComponents === 'object' ? [{
                  hidden: true,
                  prompt: 'spellcheckPlantNamePrompt3',
                  arg: Object.entries(addressComponents)
                    .filter(([_, value]) => value)
                    .map(([_, value]) => value)
                    .join(', ')
                }] : [])
              ],
              responseSchema: {
                type: "object",
                properties: {
                  botanicalname: {
                    type: "string",
                    description: "The closest botanical name to the one requested"
                  },
                  rank: {
                    type: "string",
                    description: "The taxonomic rank of the name, eg: genus, variety, family, etc."
                  }
                },
                required: ["botanicalname", "rank"]
              },
              temperature: 0.1
            },
            retry: attempt > 0 ? 1 : false,
            retryDelay: Math.pow(2, attempt) * 1000,
            onResponseError({ response }) {
              console.error(`Attempt ${attempt + 1} failed with status:`, response.status);
              throw new Error(`API request failed with status ${response.status}`);
            }
          });

          // Parse the response
          if (spellCheckResponse && 
              typeof spellCheckResponse === 'object' && 
              'botanicalname' in spellCheckResponse) {
            
            // Filter out null, empty, or "nothing found" responses
            const botanicalName = spellCheckResponse.botanicalname;
            if (!botanicalName || 
                botanicalName === 'null' || 
                botanicalName.toLowerCase() === 'nothing found' ||
                botanicalName.trim() === '') {
              return []; // Return empty array instead of "Nothing found" item
            }
            
            return [{
              value: botanicalName,
              label: botanicalName,
              group: normalizeRank(spellCheckResponse?.rank || 'AI Suggestion'),
              key: botanicalName
            }];
          } else {
            if (spellCheckResponse && typeof spellCheckResponse === 'object' && 'text' in spellCheckResponse) {
              try {
                const textContent = (spellCheckResponse as { text: string }).text;
                const parsedContent = JSON.parse(textContent);
                
                if (parsedContent && parsedContent.botanicalname) {
                  const botanicalName = parsedContent.botanicalname;
                  
                  // Filter out null, empty, or "nothing found" responses
                  if (!botanicalName || 
                      botanicalName === 'null' || 
                      botanicalName.toLowerCase() === 'nothing found' ||
                      botanicalName.trim() === '') {
                    return []; // Return empty array instead of "Nothing found" item
                  }
                  
                  return [{
                    value: botanicalName,
                    label: botanicalName,
                    group: normalizeRank(parsedContent.rank || 'AI Suggestion'),
                    key: botanicalName
                  }];
                }
              } catch (parseError) {
                console.error('Error parsing suggestion text:', parseError);
              }
            }
          }
          
          throw new Error('Invalid response format from Gemini API');
          
        } catch (requestError) {
          console.error(`Attempt ${attempt + 1} failed:`, requestError);
          if (attempt === 2) {
            console.error('All retry attempts failed');
            return [];
          }
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error with Gemini API spell check:', error);
      return [];
    }
  };

  // Create a debounced version of the spell check function using VueUse
  const spellCheckBotanicalName = useDebounceFn(
    _spellCheckBotanicalNameInternal,
    100 // 100ms debounce as requested
  );

  return {
    isLoaded,
    createCellEditorParams,
    createAutocompleteColumns,
    createSimpleAutocomplete,
    createStaticAutocomplete,
    createApiAutocomplete,
    createBotanicalNameAutocomplete,
    createCommonNameAutocomplete,
    spellCheckBotanicalName
  }
} 